import React, { useEffect, useState } from 'react';
import {
  Drawer,
  AppBar,
  Toolbar,
  IconButton,
  Box,
  CssBaseline,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  MenuOpen as MenuOpenIcon,
  Close as CloseIcon,
  Dashboard as DashboardIcon,
  AccountCircle as AccountCircleIcon,
  People as PeopleIcon,
  ExpandLess,
  ExpandMore,
  Inventory2 as ProductsIcon,
  Link as LinkIcon,
  Photo as SelfieIcon,
  Store as BrandIcon,
  Category as CategoryIcon,
  AccountTree as SubcategoryIcon,
  ShoppingCart as CartIcon,
} from '@mui/icons-material';

import Logo from '../assets/images/logo.svg';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Avatar, Menu, MenuItem } from '@mui/material';
import { getAdminDetailsApi } from '../components/actionCreator';
import { formatUserName } from '../utils/userDisplayName';
import MGlo from '../assets/images/HQ.png';

const DRAWER_WIDTH_OPEN = 240;
const DRAWER_WIDTH_CLOSED = 72;

function DashboardLayout() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const [desktopOpen, setDesktopOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const showLabels = isDesktop ? desktopOpen : true;
  const drawerPaperWidth = isDesktop
    ? desktopOpen
      ? DRAWER_WIDTH_OPEN
      : DRAWER_WIDTH_CLOSED
    : DRAWER_WIDTH_OPEN;

  const toggleDrawer = () => {
    if (isDesktop) {
      setDesktopOpen((prev) => !prev);
    } else {
      setMobileOpen((prev) => !prev);
    }
  };

  const closeMobileDrawer = () => {
    if (!isDesktop) setMobileOpen(false);
  };

  const usersSectionActive =
    location.pathname.includes('/creators') || location.pathname.includes('/classicuser');

  const handleUsersClick = () => {
    if (isDesktop && !desktopOpen) {
      setDesktopOpen(true);
      setUserMenuOpen(true);
      return;
    }
    if (!showLabels) return;
    setUserMenuOpen((prev) => !prev);
  };

  const isActive = (path) => location.pathname === path;
  const [anchorEl, setAnchorEl] = useState(null);
  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const [meProfile, setMeProfile] = useState(null);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);

  useEffect(() => {
    closeMobileDrawer();
  }, [location.pathname, isDesktop]);

  useEffect(() => {
    if (!showLabels) {
      setUserMenuOpen(false);
      return;
    }
    if (usersSectionActive) {
      setUserMenuOpen(true);
    }
  }, [showLabels, usersSectionActive]);

  const loadMyProfile = (onDone) => {
    const id = localStorage.getItem('admin_id');
    if (!id) {
      setMeProfile(null);
      onDone?.();
      return;
    }
    getAdminDetailsApi(id, (res) => {
      if (res?.error || res?.success === false) {
        setMeProfile(null);
        onDone?.();
        return;
      }
      const admin = res?.data !== undefined ? res.data : res;
      setMeProfile(admin && typeof admin === 'object' ? admin : null);
      onDone?.();
    });
  };

  useEffect(() => {
    if (!localStorage.getItem('token') || !localStorage.getItem('admin_id')) return;
    loadMyProfile();
  }, []);

  const handleOpenProfile = () => {
    handleMenuClose();
    setProfileDialogOpen(true);
    setProfileLoading(true);
    loadMyProfile(() => setProfileLoading(false));
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('admin_id');
    localStorage.removeItem('role');
    localStorage.removeItem('refresh_token');
    sessionStorage.removeItem('token');
    handleMenuClose();
    navigate('/login');
  };

  const navigateTo = (path) => {
    navigate(path);
    closeMobileDrawer();
  };

  const drawerNav = (
    <>
      <Toolbar />
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <List dense={!showLabels}>
          <SidebarItem icon={<DashboardIcon />} text="Dashboard" path="/dashboard" />
          <SidebarItem icon={<AccountCircleIcon />} text="Admin Users" path="/adminusers" />

          <ListItem
            button
            onClick={handleUsersClick}
            title={!showLabels ? 'Users' : undefined}
            sx={getListItemStyle(usersSectionActive)}
          >
            <ListItemIcon
              sx={{
                ...getIconStyle(usersSectionActive),
                minWidth: showLabels ? 56 : 'auto',
                justifyContent: 'center',
              }}
            >
              <PeopleIcon />
            </ListItemIcon>
            {showLabels && <ListItemText primary="Users" />}
            {showLabels && (userMenuOpen ? <ExpandLess /> : <ExpandMore />)}
          </ListItem>
          {showLabels && (
            <Collapse in={userMenuOpen} timeout="auto" unmountOnExit>
              <List disablePadding sx={{ bgcolor: '#fafafa' }}>
                <SidebarItem text="Creator" path="/creators" nested />
                <SidebarItem text="Classic User" path="/classicuser" nested />
              </List>
            </Collapse>
          )}

          <SidebarItem icon={<ProductsIcon />} text="Products" path="/products" />
          <SidebarItem icon={<LinkIcon />} text="Link Items" path="/linkitems" />
          <SidebarItem icon={<SelfieIcon />} text="Selfies" path="/selfies" />
          <SidebarItem icon={<CartIcon />} text="User Cart" path="/usercart" />
          <SidebarItem icon={<BrandIcon />} text="Brands" path="/brands" />
          <SidebarItem icon={<CategoryIcon />} text="Categories" path="/categories" />
          <SidebarItem icon={<SubcategoryIcon />} text="Subcategories" path="/subcategories" />
        </List>
      </Box>
    </>
  );

  function SidebarItem({ icon, text, path, nested = false }) {
    if (nested && !showLabels) return null;

    const active = isActive(path);
    return (
      <ListItem
        button
        onClick={() => navigateTo(path)}
        title={!showLabels ? text : undefined}
        sx={{
          pl: nested ? 4 : showLabels ? 2 : 1.5,
          pr: showLabels ? 2 : 1,
          py: nested ? 0.75 : 1,
          minHeight: nested ? 40 : 48,
          justifyContent: showLabels ? 'flex-start' : 'center',
          backgroundColor: active ? '#2A2A2A' : 'transparent',
          color: active ? 'white' : 'inherit',
          '&:hover': {
            backgroundColor: active ? '#2A2A2A' : nested ? '#eeeeee' : '#f0f0f0',
          },
        }}
      >
        {icon && (
          <ListItemIcon
            sx={{
              color: active ? 'white' : 'inherit',
              minWidth: showLabels ? 56 : 'auto',
              justifyContent: 'center',
            }}
          >
            {icon}
          </ListItemIcon>
        )}
        {showLabels && (
          <ListItemText
            primary={text}
            primaryTypographyProps={{
              noWrap: true,
              fontSize: nested ? '0.875rem' : '0.9375rem',
            }}
          />
        )}
      </ListItem>
    );
  }

  function getListItemStyle(isActiveItem) {
    return {
      backgroundColor: isActiveItem ? '#2A2A2A' : 'transparent',
      color: isActiveItem ? 'white' : 'inherit',
      justifyContent: showLabels ? 'flex-start' : 'center',
      '&:hover': {
        backgroundColor: isActiveItem ? '#2A2A2A' : '#f0f0f0',
      },
    };
  }

  function getIconStyle(isActiveItem) {
    return {
      color: isActiveItem ? 'white' : 'inherit',
    };
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          backgroundColor: '#fff',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, px: { xs: 1, sm: 2 } }}>
          <IconButton
            edge="start"
            color="inherit"
            onClick={toggleDrawer}
            aria-label="Toggle navigation"
            sx={{ color: '#2A2A2A', mr: { xs: 0.5, sm: 1 } }}
          >
            {isDesktop ? (
              desktopOpen ? (
                <CloseIcon />
              ) : (
                <MenuOpenIcon />
              )
            ) : mobileOpen ? (
              <CloseIcon />
            ) : (
              <MenuOpenIcon />
            )}
          </IconButton>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.5, sm: 1 },
              minWidth: 0,
              flex: 1,
            }}
          >
            <Box
              component="img"
              src={Logo}
              alt="logo"
              sx={{ height: { xs: 32, sm: 40 }, width: 'auto', flexShrink: 0 }}
            />
            <Box
              component="img"
              src={MGlo}
              alt="MGlo"
              sx={{
                height: { xs: 32, sm: 40 },
                width: 'auto',
                flexShrink: 0,
                display: { xs: 'none', sm: 'block' },
              }}
            />
          </Box>
          <IconButton onClick={handleMenuOpen} sx={{ color: '#2A2A2A' }} aria-label="Account menu">
            {meProfile?.picture ? (
              <Box
                component="img"
                src={meProfile.picture}
                alt=""
                sx={{ height: 36, width: 36, borderRadius: '50%', objectFit: 'cover' }}
              />
            ) : (
              <Avatar
                alt={formatUserName(meProfile) || meProfile?.email || 'User'}
                sx={{ width: 36, height: 36 }}
              >
                {(meProfile?.first_name?.[0] || meProfile?.email?.[0] || '?').toUpperCase()}
              </Avatar>
            )}
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={handleOpenProfile}>Profile</MenuItem>
            <MenuItem onClick={handleLogout}>Logout</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Dialog open={profileDialogOpen} onClose={() => setProfileDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ background: '#353535', color: '#fff' }}>My profile</DialogTitle>
        <DialogContent dividers>
          {profileLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : meProfile ? (
            <Box
              component="table"
              sx={{
                width: '100%',
                borderCollapse: 'collapse',
                '& td': { wordBreak: 'break-word' },
              }}
            >
              <tbody>
                {[
                  ['First name', meProfile.first_name],
                  ['Last name', meProfile.last_name],
                  ['Email', meProfile.email],
                  ['Role', meProfile.role],
                  ['Account type', meProfile.account_type],
                  [
                    'Brand',
                    meProfile.brand_id && typeof meProfile.brand_id === 'object'
                      ? meProfile.brand_id.name
                      : meProfile.brand_id,
                  ],
                  [
                    'Created',
                    meProfile.created_at ? new Date(meProfile.created_at).toLocaleString() : '—',
                  ],
                  [
                    'Updated',
                    meProfile.updated_at ? new Date(meProfile.updated_at).toLocaleString() : '—',
                  ],
                ].map(([label, value]) => (
                  <tr key={label} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '10px', fontWeight: 700, width: '38%', color: '#555' }}>
                      {label}
                    </td>
                    <td style={{ padding: '10px' }}>{value ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Box>
          ) : (
            <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
              Could not load profile. Check that you are logged in and try again.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProfileDialogOpen(false)} sx={{ background: '#353535', color: '#fff' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        component="nav"
        sx={{
          width: { md: drawerPaperWidth },
          flexShrink: { md: 0 },
        }}
      >
        <Drawer
          variant={isDesktop ? 'permanent' : 'temporary'}
          open={isDesktop ? true : mobileOpen}
          onClose={closeMobileDrawer}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: drawerPaperWidth,
              boxSizing: 'border-box',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              overflow: 'hidden',
              borderRight: '1px solid',
              borderColor: 'divider',
            },
          }}
        >
          {drawerNav}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { xs: '100%', md: `calc(100% - ${drawerPaperWidth}px)` },
          maxWidth: '100%',
          p: { xs: 2, sm: 3 },
          pt: { xs: 10, sm: 11 },
          background: '#f2f5f9',
          minHeight: '100vh',
          overflowX: 'auto',
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}

export default DashboardLayout;
